import React, { useMemo } from 'react';
import { Typography } from '../../../widgets/Typography';
import { twMerge } from 'tailwind-merge';
import DownloadTemplateCard from './DownloadTemplateCard';
import Button from '../../../widgets/Button';
import { ChevronRightIcon } from '../../../../assets/icons/ChevronRightIcon';
import UploadCSVCard from './UploadCSVCard';
import OnboardingConfirmation from './OnboardingConfirmation';
import { CheckIcon } from '../../../../assets/icons/CheckIcon';
import { AnimatePresence, motion } from 'motion/react';
import {
  RosterUploadStep,
  RosterUploadStepLabels,
  RosterUploadSteps,
} from '../constants';
import { StepContentsVariants } from '../animations';
import useGetPresignedUrl from '../hooks/useGetPresignedUrl';
import useUploadToPresignedURL from '../hooks/useUploadToPresignedURL';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

export default function OnboardingStepper() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = React.useState(
    RosterUploadStep.DOWNLOAD_TEMPLATE,
  );

  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);

  const [stepDirection, setStepDirection] = React.useState<
    'forward' | 'backward'
  >('forward');

  const { mutate: uploadToPresignedURL, isPending: isUploadingToPresignedURL } =
    useUploadToPresignedURL({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['sync-files-list'] });
        setSelectedFile(null);
        navigate('/members');
      },
      onError: () => {
        alert('Failed to upload file. Please try again.');
      },
    });

  const { mutate: uploadFile, isPending: isFetchingPresignedUrl } =
    useGetPresignedUrl({
      onSuccess: (data) => {
        const presignedUrl = data?.uploadUrl;

        if (presignedUrl && selectedFile) {
          uploadToPresignedURL({ file: selectedFile, presignedUrl });
        }
      },
      onError: () => {
        alert('Failed to upload file. Please try again.');
      },
    });

  const shouldDisableNextButton = useMemo(() => {
    if (currentStep === RosterUploadStep.UPLOAD_CSV) {
      return selectedFile === null;
    }

    return false;
  }, [currentStep, selectedFile]);

  const stepMultiplier = stepDirection === 'forward' ? 1 : -1;

  const stepContent = useMemo(() => {
    switch (currentStep) {
      case RosterUploadStep.DOWNLOAD_TEMPLATE:
        return <DownloadTemplateCard />;

      case RosterUploadStep.UPLOAD_CSV:
        return (
          <UploadCSVCard
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
          />
        );

      case RosterUploadStep.CONFIRMATION:
        return <OnboardingConfirmation />;
    }
  }, [currentStep, selectedFile]);

  return (
    <div className="mt-16 flex flex-col items-center justify-start">
      <Typography variant="h3" weight="semibold">
        Member Sync Setup
      </Typography>
      <Typography variant="body-md" weight="regular" className="text-grey-700">
        Follow these steps to upload and sync your active member list.
      </Typography>
      <div className="flex flex-col items-center justify-start gap-2">
        <div className="mt-8 mb-6 flex flex-row items-center justify-center gap-32">
          {RosterUploadSteps.map((step, index) => {
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;

            return (
              <div
                key={step}
                className="group relative flex flex-col items-center justify-start"
              >
                <motion.div
                  animate={{
                    scale: isActive ? 1.2 : 1,
                    backgroundColor:
                      isActive || isCompleted
                        ? 'var(--color-blue-700)'
                        : 'var(--color-neutral-300)',
                    transition: { duration: 0.3, bounce: 0.2 },
                  }}
                  className={twMerge(
                    'relative flex h-10 w-10 items-center justify-center rounded-full',
                  )}
                >
                  {/* animated SVG line that connects the steps */}
                  <svg
                    className="absolute top-1/2 left-full -translate-y-1/2 overflow-visible group-last:hidden"
                    width="128"
                    height="2"
                    viewBox="0 0 128 2"
                  >
                    <path
                      d="M 0 1 L 128 1"
                      stroke="var(--color-neutral-300)"
                      strokeWidth="2"
                      fill="none"
                    />
                    <motion.path
                      d="M 0 1 L 128 1"
                      stroke="var(--color-blue-700)"
                      strokeWidth="2"
                      fill="none"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: isCompleted ? 1 : 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                    />
                  </svg>
                  <Typography variant="body-md" className="text-neutral-100">
                    {isCompleted ? (
                      <CheckIcon className="size-5 stroke-neutral-100" />
                    ) : (
                      index + 1
                    )}
                  </Typography>
                </motion.div>
                <div className="absolute -bottom-8">
                  <Typography
                    variant="body-sm"
                    className="text-grey-700 mt-2 text-center"
                  >
                    {RosterUploadStepLabels[step]}
                  </Typography>
                </div>
              </div>
            );
          })}
        </div>
        <AnimatePresence initial={false} mode="wait" custom={stepMultiplier}>
          <motion.div
            key={currentStep}
            variants={StepContentsVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            custom={stepMultiplier}
            className="mt-8 w-full"
          >
            {stepContent}
          </motion.div>
        </AnimatePresence>
        <div className="mt-6 flex w-full flex-row justify-between">
          <Button
            leftIcon={<ChevronRightIcon className="rotate-180" />}
            variant="outline"
            onClick={() => {
              if (currentStep === RosterUploadStep.DOWNLOAD_TEMPLATE) {
                navigate('/members');
                return;
              }

              setStepDirection('backward');
              setCurrentStep((prev) => Math.max(prev - 1, 0));
            }}
          >
            Back
          </Button>
          <Button
            disabled={shouldDisableNextButton}
            isLoading={isFetchingPresignedUrl || isUploadingToPresignedURL}
            rightIcon={<ChevronRightIcon />}
            variant="primary"
            onClick={() => {
              if (
                currentStep === RosterUploadStep.CONFIRMATION &&
                selectedFile !== null
              ) {
                uploadFile(selectedFile);
              }

              setStepDirection('forward');
              setCurrentStep((prev) =>
                Math.min(prev + 1, RosterUploadSteps.length - 1),
              );
            }}
          >
            {currentStep === RosterUploadStep.CONFIRMATION ? 'Confirm' : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  );
}
